import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BadgeForm from './components/BadgeForm';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import BadgeService from '../../services/badgeService';

const AddBadgePage = () => {
  const navigate = useNavigate();
  const { isSuperUser } = useAuth();
  const [searchParams] = useSearchParams();
  const badgeId = searchParams?.get('id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [badge, setBadge] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Load badge for editing if ID is provided
  useEffect(() => {
    if (badgeId) {
      setIsEditMode(true);
      loadBadge();
    }
  }, [badgeId]);

  // Redirect non-superusers
  useEffect(() => {
    if (!isSuperUser()) {
      navigate('/admin-home-dashboard');
    }
  }, [isSuperUser, navigate]);

  const loadBadge = async () => {
    setLoading(true);
    try {
      const { data, error } = await BadgeService?.getBadgeById(badgeId);
      if (error) {
        setError(error);
      } else {
        setBadge(data);
      }
    } catch (error) {
      console.error('Failed to load badge:', error);
      setError('Failed to load badge');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (badgeData) => {
    setSaving(true);
    setError(null);
    
    try {
      let result;
      
      if (isEditMode) {
        result = await BadgeService?.updateBadge(badgeId, badgeData);
      } else {
        result = await BadgeService?.createBadge(badgeData);
      }

      if (result?.error) {
        setError(result?.error);
      } else {
        // Success - navigate back to badge list
        navigate('/all-badges');
      }
    } catch (error) {
      console.error('Failed to save badge:', error);
      setError('Failed to save badge. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/all-badges');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Icon name="Award" size={32} className="text-blue-200" />
              </div>
              <p className="text-blue-100">Loading badge...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{isEditMode ? 'Edit Badge' : 'Add Badge'} - RoleCall</title>
        <meta name="description" content={isEditMode ? 'Edit badge details and configuration' : 'Create a new achievement badge for the RoleCall system'} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="outline"
                onClick={handleCancel}
                iconName="ArrowLeft"
                iconPosition="left"
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
              >
                Back to Badges
              </Button>
            </div>
            
            <h1 className="text-3xl font-heading font-bold text-white mb-2">
              {isEditMode ? 'Edit Badge' : 'Create New Badge'}
            </h1>
            <p className="text-blue-100 font-body">
              {isEditMode ? 'Update badge details and configuration' : 'Design an achievement badge to recognize user accomplishments'}
            </p>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Badge Form */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8">
            <BadgeForm
              badge={badge}
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
              isEditMode={isEditMode}
            />
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500 bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="HelpCircle" size={16} className="text-blue-300" />
              </div>
              <div>
                <h3 className="text-blue-100 font-medium mb-2">Badge Creation Tips</h3>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>• Choose clear, descriptive titles that explain the achievement</li>
                  <li>• Use appropriate categories: Learning (educational milestones), Streak (consistency), Mastery (skill levels), Special (unique accomplishments)</li>
                  <li>• Set point values that reflect the difficulty or importance of the achievement</li>
                  <li>• Define criteria that can be automatically tracked by the system</li>
                  <li>• Upload high-quality badge icons for better visual appeal</li>
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AddBadgePage;